import { useState, useEffect } from "react";
import { useOutletContext } from "react-router-dom";
import { Image, X } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { getGallery } from "@/lib/api";

const HERO_IMAGE = "https://images.pexels.com/photos/8224722/pexels-photo-8224722.jpeg";

const categories = [
  { value: "all", label: "Alles" },
  { value: "algemeen", label: "Algemeen" },
  { value: "activiteiten", label: "Activiteiten" },
  { value: "evenementen", label: "Evenementen" }
];

export default function SportEldoradoGalerij() {
  const context = useOutletContext();
  const isDark = context?.isDark ?? true;
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [selectedImage, setSelectedImage] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    const fetchImages = async () => {
      try {
        const params = selectedCategory !== "all" ? { category: selectedCategory } : {};
        const res = await getGallery(params);
        setImages(res.data);
      } catch (error) {
        console.error("Error fetching gallery:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchImages();
  }, [selectedCategory]);

  const handleImageClick = (image) => {
    setSelectedImage(image);
    setDialogOpen(true);
  };

  return (
    <div className="min-h-screen" data-testid="sport-eldorado-galerij-page">
      {/* Hero */}
      <section className="relative h-[40vh] min-h-[300px]">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${HERO_IMAGE})` }}
        />
        <div className="hero-gradient absolute inset-0" />
        <div className="relative h-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-end pb-12">
          <div>
            <p className="text-[hsl(48,96%,53%)] font-medium mb-2">Sport Eldorado</p>
            <h1 className="text-4xl md:text-5xl font-black text-white font-['Chivo']">GALERIJ</h1>
            <p className="text-slate-300 mt-2">Bekijk onze activiteiten en evenementen</p>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Category Filter */}
        <div className="flex flex-wrap gap-2 mb-8 justify-center">
          {categories.map((cat) => (
            <Button
              key={cat.value}
              variant={selectedCategory === cat.value ? "default" : "outline"}
              onClick={() => setSelectedCategory(cat.value)}
              className={selectedCategory === cat.value 
                ? "bg-[hsl(217,91%,60%)] hover:bg-[hsl(217,91%,50%)]" 
                : isDark ? "border-white/20 text-slate-300 hover:bg-white/10" : "border-slate-300"
              }
            >
              {cat.label}
            </Button>
          ))}
        </div>

        {/* Gallery Grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className={`aspect-square rounded-lg animate-pulse ${isDark ? 'bg-slate-800' : 'bg-slate-200'}`} />
            ))}
          </div>
        ) : images.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {images.map((image) => (
              <Card 
                key={image.id}
                className={`group cursor-pointer overflow-hidden ${isDark ? 'bg-slate-900/50 border-white/10' : 'bg-white border-slate-200 shadow-md'}`}
                onClick={() => handleImageClick(image)}
              >
                <div className="aspect-square relative overflow-hidden">
                  <img 
                    src={image.image_url} 
                    alt={image.title}
                    className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/50 transition-colors flex items-center justify-center">
                    <Image className="text-white opacity-0 group-hover:opacity-100 transition-opacity h-10 w-10" />
                  </div>
                </div>
                <CardContent className="py-4">
                  <h3 className={`font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>{image.title}</h3>
                  {image.description && (
                    <p className={`text-sm mt-1 ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
                      {image.description}
                    </p>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card className={`text-center py-16 ${isDark ? 'bg-slate-900/50 border-white/10' : 'bg-white border-slate-200 shadow-md'}`}>
            <CardContent>
              <Image className={`h-16 w-16 mx-auto mb-4 ${isDark ? 'text-slate-600' : 'text-slate-300'}`} />
              <h3 className={`text-xl font-bold mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                Geen foto's beschikbaar
              </h3>
              <p className={`${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
                Er zijn nog geen foto's toegevoegd aan deze categorie.
              </p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Image Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-4xl p-0 bg-black border-none">
          <Button
            variant="ghost"
            size="icon"
            className="absolute top-2 right-2 z-10 text-white hover:bg-white/20"
            onClick={() => setDialogOpen(false)}
          >
            <X className="h-6 w-6" />
          </Button>
          {selectedImage && (
            <div>
              <img 
                src={selectedImage.image_url} 
                alt={selectedImage.title}
                className="w-full max-h-[80vh] object-contain"
              />
              <div className="p-4 bg-black/80">
                <h3 className="text-white font-bold text-lg">{selectedImage.title}</h3>
                {selectedImage.description && (
                  <p className="text-slate-300 mt-1">{selectedImage.description}</p>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
